import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaExclamationTriangle,
  FaRedo,
  FaSave,
  FaSearchPlus,
  FaSearchMinus,
  FaCompress,
  FaBars,
  FaTimes,
  FaSync // Added Sync icon for Refresh
} from 'react-icons/fa';
import { negotiationDetails, sendNegotiation } from '../../../../services/CommonService';

const NegotiationStep6 = ({ formObj, setFormObj, setLoading, prevStep, html_content }) => {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [negotiationData, setNegotiationData] = useState(null);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // State for refresh spinner

  // Pagination & Viewer States
  const [paginatedContent, setPaginatedContent] = useState([]);
  const [isPaginating, setIsPaginating] = useState(true);

  // Default Zoom
  const [zoomLevel, setZoomLevel] = useState(1);
  const [activePage, setActivePage] = useState(0);

  // Mobile Sidebar State
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  // Track screen size for layout switching
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const viewerContainerRef = useRef(null);
  const pageRefs = useRef([]);

  // CONSTANTS: A4 Size in Pixels (approx)
  const A4_WIDTH_PX = 794;
  const A4_HEIGHT_PX = 1123;

  // --- 1. AUTO-FIT & RESIZE LOGIC ---
  useEffect(() => {
    const handleResize = () => {
      const containerWidth = window.innerWidth;
      const mobileStatus = containerWidth < 768;

      setIsMobile(mobileStatus);

      // Auto Zoom Logic
      if (mobileStatus) {
        const fitScale = (containerWidth - 30) / A4_WIDTH_PX;
        setZoomLevel(fitScale);
      }
    };

    handleResize(); // Run on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (uuid) fetchNegotiationDetails();
    else {
      setError('No negotiation UUID provided');
      setLoading(false);
      toast.error('No negotiation UUID provided');
    }
    // eslint-disable-next-line
  }, [uuid]);

  useEffect(() => {
    const rawContent = html_content || formObj?.html_content || '';
    if (rawContent) {
      splitContentToPages(rawContent);
    } else {
      setPaginatedContent([]);
      setIsPaginating(false);
    }
  }, [html_content, formObj]);

  const fetchNegotiationDetails = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) setIsRefreshing(true); // Local spinner for refresh button
      else setLoading(true); // Global loader for initial load

      setError(null);
      const response = await negotiationDetails({ uuid });
      const data = response?.data?.data;
      if (!data) throw new Error('No data received from server');
      setNegotiationData(data);
      if (setFormObj) {
        setFormObj(prev => ({
          ...prev,
          negotiation_details: data
        }));
      }
      if (isManualRefresh) toast.success('Data refreshed successfully');
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to load negotiation details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const splitContentToPages = (htmlString) => {
    setIsPaginating(true);

    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.width = '210mm';
    tempDiv.style.padding = '20mm';
    tempDiv.style.fontSize = '12pt';
    tempDiv.style.lineHeight = '1.6';
    tempDiv.style.fontFamily = '"Times New Roman", Times, serif';
    tempDiv.style.color = '#000000';
    tempDiv.innerHTML = htmlString;
    document.body.appendChild(tempDiv);

    const pages = [];
    let currentPageNodes = [];
    let currentHeight = 0;
    const PAGE_LIMIT = 960;

    Array.from(tempDiv.children).forEach((child) => {
      const childHeight = child.offsetHeight;
      const style = window.getComputedStyle(child);
      const margin = parseInt(style.marginTop) + parseInt(style.marginBottom);
      const totalHeight = childHeight + margin;

      if (currentHeight + totalHeight > PAGE_LIMIT && currentPageNodes.length > 0) {
        pages.push(currentPageNodes.map(node => node.outerHTML).join(''));
        currentPageNodes = [];
        currentHeight = 0;
      }

      currentPageNodes.push(child);
      currentHeight += totalHeight;
    });

    if (currentPageNodes.length > 0) {
      pages.push(currentPageNodes.map(node => node.outerHTML).join(''));
    }

    document.body.removeChild(tempDiv);
    setPaginatedContent(pages);
    setIsPaginating(false);
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 2.0));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.3));

  const handleResetZoom = () => {
    if (window.innerWidth < 768) {
      const fitScale = (window.innerWidth - 30) / A4_WIDTH_PX;
      setZoomLevel(fitScale);
    } else {
      setZoomLevel(0.8);
    }
  };

  const scrollToPage = (index) => {
    setActivePage(index);
    if (pageRefs.current[index]) {
      pageRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (isMobile) {
      setShowMobileSidebar(false);
    }
  };

  const handleSaveAndExit = async () => {
    if (!negotiationData?.id) return toast.error('Negotiation ID is missing');
    setIsSaving(true);
    try {
      if (setFormObj) {
        setFormObj(prev => ({ ...prev, status: 'draft', last_saved: new Date().toISOString() }));
      }
      toast.success('Negotiation saved as draft successfully!');
      navigate('/manage/negotiation');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSend = async () => {
    if (isSending) return;
    if (!negotiationData?.id) return toast.error('Negotiation ID missing');
    const recipients = negotiationData?.recipient_list || [];
    if (recipients.length === 0) {
      toast.error('Please add at least one recipient before sending');
      return;
    }
    setIsSending(true);
    setLoading(true);
    try {
      const payload = { negotiation_id: negotiationData.id };
      const response = await sendNegotiation(payload);
      toast.success(response.data?.message || 'Negotiation sent successfully!');
      if (setNegotiationData) setNegotiationData(prev => ({ ...prev, status: 'sent' }));
      setTimeout(() => navigate('/manage/negotiation'), 800);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to send negotiation');
    } finally {
      setLoading(false);
      setIsSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
  };

  const getRecipientEmails = () =>
    negotiationData?.recipient_list?.map(r => r.email).filter(Boolean).join(', ') || '';

  const handleBack = () => (prevStep ? prevStep() : navigate(-1));

  if (error) {
    return (
      <div className="ns6-error-card text-center p-5">
        <FaExclamationTriangle size={32} className="text-danger mb-3" />
        <h2>Oops! Something Went Wrong</h2>
        <p className="text-muted">{error}</p>
        <button className="btn btn-outline-secondary mt-3" onClick={() => fetchNegotiationDetails(false)}>
          <FaRedo className="me-2" /> Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="negotiation-container negotiation-step6" style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Header */}
      <div className="negotiation-step1-header mb-3 pb-3">
        <div className="negotiation-header-left">
          <h4 className="step1-title mb-0 fs-5">Review & Send</h4>
        </div>
        <div className="negotiation-header-right">
          <span className="step-indicator">Step 6 of 6</span>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '15px', overflow: 'hidden', minWidth: 0 }}>

        {/* --- Card 1: Summary Bar (FIXED & IMPROVED) --- */}
        <div className="card shadow-sm border-0 flex-shrink-0">
          <div className="card-body p-2 d-flex align-items-center gap-3 flex-wrap">

            {/* 1. Negotiation Name */}
            <div className="d-flex flex-column justify-content-center" style={{ minWidth: '150px' }}>
              <div className='d-flex align-items-center gap-2'>
                <p className="text-muted" style={{ fontSize: '0.9rem' }}>NEGOTIATION : </p>
                <span className="fw-semibold text-dark text-truncate" style={{ maxWidth: '200px' }} title={negotiationData?.negotiation_name}>
                  {negotiationData?.negotiation_name || 'N/A'}
                </span>
              </div>

            </div>

            {/* Vertical Divider */}
            <div className="vr d-none d-md-block" style={{ height: '30px', opacity: 0.2 }}></div>

            {/* 2. Due Date */}
            <div className="d-flex flex-column justify-content-center" style={{ minWidth: '100px' }}>
              <div className='d-flex align-items-center gap-2'>
                <p className="text-muted" style={{ fontSize: '0.9rem' }}> DUE DATE : </p>
                <span className="fw-semibold text-dark">
                  {formatDate(negotiationData?.due_date)}
                </span>
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="vr d-none d-md-block" style={{ height: '30px', opacity: 0.2 }}></div>

            {/* 3. Collaborators (With Refresh & Scroll) */}
            <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>COLLABORATORS</small>
              </div>

              {/* Scrollable Container for Emails */}
              <div
                className="d-flex gap-1 align-items-center custom-scrollbar"
                style={{
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  paddingBottom: '2px' // Spacing for scrollbar
                }}
              >
                {getRecipientEmails() ? (
                  getRecipientEmails().split(',').filter(e => e.trim()).map((email, idx) => (
                    <span
                      className="ns6-email-pill badge bg-light text-dark border fw-normal"
                      key={idx}
                      style={{ flexShrink: 0 }} // Prevents shrinking
                    >
                      {email.trim()}
                    </span>
                  ))
                ) : (
                  <span className="text-muted small">-</span>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* --- Card 2: Document Viewer --- */}
        <div className="card shadow-sm border-0 flex-grow-1" style={{ overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>

          {/* Toolbar */}
          <div className="card-header bg-dark text-white p-1 px-3 d-flex justify-content-between align-items-center flex-shrink-0" style={{ height: '40px' }}>
            <div className="d-flex align-items-center gap-2">
              {/* Bars Icon - Only Visible on Mobile */}
              <button
                className="btn btn-sm btn-link text-white p-0 d-md-none me-2"
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
              >
                {showMobileSidebar ? <FaTimes /> : <FaBars />}
              </button>
              <span className="fw-semibold small">Preview</span>
            </div>

            <div className="d-flex gap-2 align-items-center">
              <button className="btn btn-sm btn-link text-white p-0" onClick={handleZoomOut} title="Zoom Out"><FaSearchMinus /></button>
              <span className="mx-1 small" style={{ minWidth: '35px', textAlign: 'center' }}>{Math.round(zoomLevel * 100)}%</span>
              <button className="btn btn-sm btn-link text-white p-0" onClick={handleZoomIn} title="Zoom In"><FaSearchPlus /></button>
              <button className="btn btn-sm btn-link text-white ms-2 p-0" onClick={handleResetZoom} title="Reset"><FaCompress /></button>
            </div>
          </div>

          {/* Viewer Container */}
          <div className="d-flex bg-secondary bg-opacity-25 flex-grow-1 position-relative" style={{ overflow: 'hidden' }}>

            {/* --- SIDEBAR (THUMBNAILS) --- */}
            <div
              className="bg-dark border-end overflow-auto custom-scrollbar"
              style={{
                width: '180px',
                height: '100%',
                padding: '10px',
                flexDirection: 'column',
                gap: '10px',
                alignItems: 'center',
                flexShrink: 0,
                zIndex: 1000,
                display: isMobile ? (showMobileSidebar ? 'flex' : 'none') : 'flex',
                position: isMobile ? 'absolute' : 'relative',
                left: 0,
                top: 0
              }}
            >
              {isPaginating ? (
                <div className="text-white small">Loading...</div>
              ) : (
                paginatedContent.map((pageHtml, index) => (
                  <div
                    key={index}
                    onClick={() => scrollToPage(index)}
                    style={{
                      cursor: 'pointer',
                      border: activePage === index ? '2px solid #0d6efd' : '1px solid #555',
                      borderRadius: '2px',
                      opacity: activePage === index ? 1 : 0.6
                    }}
                  >
                    <div style={{ width: '100px', height: '141px', backgroundColor: 'white', overflow: 'hidden', position: 'relative' }}>
                      <div
                        style={{ transform: 'scale(0.12)', transformOrigin: 'top left', width: '210mm', height: '297mm', padding: '20mm', pointerEvents: 'none', color: '#000' }}
                        dangerouslySetInnerHTML={{ __html: pageHtml }}
                      />
                    </div>
                    <div className="text-center text-white-50" style={{ fontSize: '10px' }}>{index + 1}</div>
                  </div>
                ))
              )}
            </div>

            {/* Mobile Sidebar Overlay */}
            {isMobile && showMobileSidebar && (
              <div
                onClick={() => setShowMobileSidebar(false)}
                style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 999 }}
              />
            )}

            {/* Main Page Viewer */}
            <div
              className="flex-grow-1 overflow-auto d-flex flex-column p-2 p-md-4 bg-secondary bg-opacity-25"
              ref={viewerContainerRef}
              style={{ position: 'relative' }}
            >
              {isPaginating ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <div className="spinner-border text-primary" role="status"></div>
                </div>
              ) : (
                paginatedContent.map((pageHtml, index) => (
                  <div
                    key={index}
                    ref={(el) => (pageRefs.current[index] = el)}
                    className="shadow mb-4 bg-white"
                    style={{
                      width: `${A4_WIDTH_PX * zoomLevel}px`,
                      height: `${A4_HEIGHT_PX * zoomLevel}px`,
                      margin: '0 auto 20px auto',
                      overflow: 'hidden',
                      transition: 'width 0.2s, height 0.2s',
                      flexShrink: 0
                    }}
                  >
                    <div
                      style={{
                        width: `${A4_WIDTH_PX}px`,
                        height: `${A4_HEIGHT_PX}px`,
                        padding: '20mm',
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: 'top left',
                        color: '#000000',
                        backgroundColor: '#ffffff',
                        transition: 'transform 0.2s'
                      }}
                    >
                      <div className="ck-content" dangerouslySetInnerHTML={{ __html: pageHtml }} />
                    </div>
                  </div>
                ))
              )}
            </div>

          </div>
        </div>

      </div>

      <div className="sleeve-step-buttons mt-3 flex-shrink-0">
        <button onClick={handleBack} className="sleeve-btn-back" type="button">Back</button>
        <div className="sleeve-step-buttons-right">
          <button className="sleeve-btn-save d-flex align-items-center " onClick={handleSaveAndExit} disabled={isSaving} type="button">
            {isSaving ? <span className="spinner-border spinner-border-sm" /> : <FaSave />} Save & Exit
          </button>
          <button className="sleeve-btn-next d-flex align-items-center " onClick={handleSend} disabled={isSending} type="button">
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default NegotiationStep6;